import React, { useState, useEffect, useRef } from 'react'
import { X, User, ArrowRight, CheckCircle, Eye, EyeOff, Mail, Sparkles } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock'
import { dataPersistence } from '../../services/dataPersistence'
import { PasswordStrengthIndicator } from './PasswordStrengthIndicator'
import { passwordSchema, scorePassword } from '../../lib/passwordValidation'
import { supabase } from '../../lib/supabase'
import toast from 'react-hot-toast'

const RESEND_COOLDOWN_SECONDS = 30

const US_PHONE_DIGITS_PATTERN = /^\d{10}$|^1\d{10}$/

function validateUSPhone(phone: string): boolean {
  const digits = phone.replace(/\D/g, '')
  return US_PHONE_DIGITS_PATTERN.test(digits)
}

interface RegisterModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  prefilledEmail?: string
}

type ModalView = 'register' | 'verify-email'

interface FormData {
  firstName: string
  lastName: string
  phone: string
  email: string
  password: string
  confirmPassword: string
  agreeToTerms: boolean
}

export const RegisterModal: React.FC<RegisterModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  prefilledEmail = '',
}) => {
  useBodyScrollLock(isOpen)
  const navigate = useNavigate()
  const [view, setView] = useState<ModalView>('register')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [submittedEmail, setSubmittedEmail] = useState('')
  const [resendCooldown, setResendCooldown] = useState(0)
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    phone: '',
    email: prefilledEmail,
    password: '',
    confirmPassword: '',
    agreeToTerms: false,
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (prefilledEmail) {
      setFormData((prev) => ({ ...prev, email: prefilledEmail }))
    }
  }, [prefilledEmail])

  useEffect(() => {
    if (!isOpen) {
      setView('register')
      setFormError(null)
      setResendCooldown(0)
      if (cooldownRef.current) clearInterval(cooldownRef.current)
    }
  }, [isOpen])

  useEffect(() => {
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current)
    }
  }, [])

  const startResendCooldown = () => {
    setResendCooldown(RESEND_COOLDOWN_SECONDS)
    cooldownRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          if (cooldownRef.current) clearInterval(cooldownRef.current)
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required'
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required'
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email address'
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required'
    } else if (!validateUSPhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid US phone number'
    }
    if (!formData.password) {
      newErrors.password = 'Password is required'
    } else {
      const passwordResult = passwordSchema.safeParse(formData.password)
      if (!passwordResult.success) {
        newErrors.password = passwordResult.error.errors[0].message
      }
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }
    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms and conditions'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setFormError(null)

    if (!validateForm()) return

    setSubmitting(true)

    try {
      const { error } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            firstName: formData.firstName,
            lastName: formData.lastName,
          },
        },
      })
      if (error) {
        setFormError(error.message || 'Registration failed. Please try again.')
        return
      }

      dataPersistence.merge({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
      })

      setSubmittedEmail(formData.email)
      setView('verify-email')
      startResendCooldown()
    } catch {
      setFormError('Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }))
    }
  }

  const handleMagicLinkContinue = () => {
    onSuccess()
    navigate('/app')
  }

  const handleResend = async () => {
    if (resendCooldown > 0) return
    try {
      const { error } = await supabase.auth.signUp({
        email: submittedEmail,
        password: formData.password,
        options: {
          data: {
            firstName: formData.firstName,
            lastName: formData.lastName,
          },
        },
      })
      if (error) {
        toast.error('Resend failed. Please try again.')
        return
      }
      toast.success('Verification email resent!')
      startResendCooldown()
    } catch {
      toast.error('Resend failed. Please try again.')
    }
  }

  if (!isOpen) return null

  if (view === 'verify-email') {
    return (
      <div
        className="modal-overlay"
        style={{ zIndex: 10000 }}
      >
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md p-6 sm:p-8 relative animate-in fade-in zoom-in duration-200">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-slate-500 dark:text-slate-400" />
          </button>

          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-gradient-to-br from-[#69adba] to-purple-500 rounded-full flex items-center justify-center mx-auto mb-5 shadow-lg shadow-[#69adba]/30">
              <Mail className="h-10 w-10 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2">
              Check your email
            </h2>
            <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
              We sent a verification link to{' '}
              <span className="font-semibold text-slate-800 dark:text-slate-200 break-all">
                {submittedEmail}
              </span>
            </p>
          </div>

          <Button
            type="button"
            onClick={handleMagicLinkContinue}
            className={[
              'w-full py-4 px-8 font-semibold text-base rounded-xl',
              'bg-gradient-to-r from-[#69adba] to-purple-500',
              'hover:from-[#5a9aa6] hover:to-purple-600',
              'text-white shadow-lg shadow-[#69adba]/40',
              'hover:shadow-xl hover:shadow-[#69adba]/50',
              'transition-all duration-200',
              'flex items-center justify-center gap-2',
            ].join(' ')}
          >
            <Sparkles className="h-5 w-5" />
            Verify and continue
            <ArrowRight className="h-5 w-5" />
          </Button>

          <div className="mt-5 text-center">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Didn&apos;t get the email?{' '}
              <button
                type="button"
                onClick={handleResend}
                disabled={resendCooldown > 0}
                className={[
                  'font-medium underline transition-colors',
                  resendCooldown > 0
                    ? 'text-slate-400 dark:text-slate-500 cursor-not-allowed no-underline'
                    : 'text-[#69adba] hover:text-[#5a9aa6]',
                ].join(' ')}
              >
                {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend'}
              </button>
            </p>
            <button
              type="button"
              onClick={handleMagicLinkContinue}
              className="mt-3 text-xs text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors underline"
            >
              Already verified? Continue to dashboard
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className="modal-overlay"
      style={{ zIndex: 10000 }}
    >
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md p-6 sm:p-8 relative animate-in fade-in zoom-in duration-200">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
        >
          <X className="h-5 w-5 text-slate-500 dark:text-slate-400" />
        </button>

        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-gradient-to-br from-[#69adba] to-[#5a9aa6] rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <User className="h-8 w-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2">
            Create Your Account
          </h2>
          <p className="text-slate-600 dark:text-slate-400">
            Complete your profile to start your 1031 exchange
          </p>
        </div>

        {formError ? (
          <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
            <p className="text-sm text-red-700 dark:text-red-400">{formError}</p>
          </div>
        ) : null}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label
                htmlFor="firstName"
                className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
              >
                First Name
              </label>
              <Input
                id="firstName"
                type="text"
                value={formData.firstName}
                onChange={(e) => handleChange('firstName', e.target.value)}
                placeholder="John"
                disabled={submitting}
              />
              {errors.firstName ? (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.firstName}</p>
              ) : null}
            </div>

            <div>
              <label
                htmlFor="lastName"
                className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
              >
                Last Name
              </label>
              <Input
                id="lastName"
                type="text"
                value={formData.lastName}
                onChange={(e) => handleChange('lastName', e.target.value)}
                placeholder="Doe"
                disabled={submitting}
              />
              {errors.lastName ? (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.lastName}</p>
              ) : null}
            </div>
          </div>

          <div>
            <label
              htmlFor="phone"
              className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              Phone Number
            </label>
            <Input
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              placeholder="(555) 123-4567"
              disabled={submitting}
            />
            {errors.phone ? (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.phone}</p>
            ) : null}
          </div>

          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              Email Address (Username)
            </label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="you@example.com"
              disabled={true}
              className="bg-slate-50 dark:bg-slate-900 cursor-not-allowed opacity-75"
            />
            {errors.email ? (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.email}</p>
            ) : null}
          </div>

          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              Password
            </label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => handleChange('password', e.target.value)}
                placeholder="Minimum 10 characters"
                disabled={submitting}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
            {errors.password ? (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.password}</p>
            ) : null}
            <PasswordStrengthIndicator password={formData.password} />
          </div>

          <div>
            <label
              htmlFor="confirmPassword"
              className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              Confirm Password
            </label>
            <div className="relative">
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={(e) => handleChange('confirmPassword', e.target.value)}
                placeholder="Re-enter password"
                disabled={submitting}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
                tabIndex={-1}
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
            {errors.confirmPassword ? (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.confirmPassword}</p>
            ) : null}
          </div>

          <div className="pt-2">
            <label className="flex items-start space-x-2 cursor-pointer">
              <div className="relative flex items-center">
                <input
                  type="checkbox"
                  id="agreeToTerms"
                  checked={formData.agreeToTerms}
                  onChange={(e) => handleChange('agreeToTerms', e.target.checked)}
                  disabled={submitting}
                  className="sr-only peer"
                />
                <div
                  className={[
                    'w-5 h-5 border-2 rounded flex items-center justify-center transition-all',
                    formData.agreeToTerms
                      ? 'bg-[#69adba] border-[#69adba]'
                      : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800',
                    errors.agreeToTerms ? 'border-red-500' : '',
                  ].join(' ')}
                >
                  {formData.agreeToTerms ? (
                    <CheckCircle className="h-4 w-4 text-white" />
                  ) : null}
                </div>
              </div>
              <span className="text-xs text-slate-600 dark:text-slate-400 leading-tight">
                I agree to the{' '}
                <Link
                  to="/legal"
                  target="_blank"
                  className="text-[#69adba] hover:text-[#5a9aa6] underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  Terms of Service
                </Link>
                {' '}and{' '}
                <Link
                  to="/legal"
                  target="_blank"
                  className="text-[#69adba] hover:text-[#5a9aa6] underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  Privacy Policy
                </Link>
              </span>
            </label>
            {errors.agreeToTerms ? (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                <X className="h-3 w-3" />
                {errors.agreeToTerms}
              </p>
            ) : null}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-[#69adba] hover:bg-[#5a9aa6] text-white"
              disabled={
                submitting ||
                !formData.agreeToTerms ||
                scorePassword(formData.password).score < 3
              }
            >
              {submitting ? (
                'Creating Account...'
              ) : (
                <>
                  Create Account
                  <ArrowRight className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
